import React, { forwardRef, useRef, useImperativeHandle, useMemo, useState, useEffect } from "react";
import {
  useStripe,
  PaymentRequestButtonElement
} from "@stripe/react-stripe-js";
import { isChrome, isAndroid, isSafari, isIOS13, isIPad13, isIPhone13, isIPod13, isIOS, isBrowser, isMobile } from 'react-device-detect';
import { H6 } from "../../../../components/headings";

const useOptions = paymentRequest => {
  const options = useMemo(
    () => ({
      paymentRequest,
      style: {
        paymentRequestButton: {
          theme: "dark",
          height: "34px",
          width: "120px",
          type: "donate",
          color: "green"
        }
      }
    }),
    [paymentRequest]
  );

  return options;
};

const usePaymentRequest = ({ options, setLoader, onPaymentMethod }) => {
  const stripe = useStripe();
  const [paymentRequest, setPaymentRequest] = useState(null);
  const [canMakePayment, setCanMakePayment] = useState(false);

  useEffect(() => {
    if (stripe && paymentRequest === null) {
      const pr = stripe.paymentRequest(options);
      setPaymentRequest(pr);
    }
  }, [stripe, options, paymentRequest]);

  useEffect(() => {
    let subscribed = true;
    if (paymentRequest) {
      paymentRequest.canMakePayment().then(res => {
        setLoader(false)
        if (res && subscribed) {
          setCanMakePayment(true);
        }
      });
    }

    return () => {
      subscribed = false;
    };
  }, [paymentRequest]);

  useEffect(() => {
    if (paymentRequest) {
      paymentRequest.on("paymentmethod", onPaymentMethod);
    }
    return () => {
      if (paymentRequest) {
        paymentRequest.off("paymentmethod", onPaymentMethod);
      }
    };
  }, [paymentRequest, onPaymentMethod]);

  return canMakePayment ? paymentRequest : null;
};

export const PaymentRequestForm = forwardRef((props, ref) => {
  const stripe = useStripe();
  const [paymentMethodId, setPaymentMethodId] = useState(null);
  const [buttonLoading, setButtonLoading] = useState(true);
  const [googleApplePayLabel, setGoogleApplePayLabel] = useState("");
  const paymentRequest = usePaymentRequest({
    options: {
      country: props.country_code,
      currency: props.currency_code,
      total: {
        label: 'Donation Amount',
        amount: (props.donation_amount*100),
      }
    },
    setLoader: setButtonLoading,
    onPaymentMethod: ({ complete, paymentMethod, ...data }) => {
      props.handlePayment(paymentMethod.id)
      setPaymentMethodId(paymentMethod.id);
      complete("success");
    }
  });

  useEffect(() => {
    if (isSafari || isIOS13 || isIPad13 || isIPhone13 || isIPod13 || isIOS) {
      setGoogleApplePayLabel("Apple")
    }
    else if (isChrome || isAndroid) {
      setGoogleApplePayLabel("Google")
    }
  }, [])

  useImperativeHandle(ref, () => ({
    async handleToken(intent_client_secret, is_recurring) {
      if (is_recurring) {
        const { paymentIntent, error } = await stripe.confirmCardPayment(intent_client_secret);
        if (error) {
          //payment failed
        } else {
          const { paymentIntent } = await stripe.retrievePaymentIntent(intent_client_secret);
          if (paymentIntent && paymentIntent.status === 'succeeded') {
            return paymentIntent
          }
          return paymentIntent;
        }
      }
      const { paymentIntent, error: confirmError } = await stripe.confirmCardPayment(
        intent_client_secret,
        { payment_method: paymentMethodId },
        { handleActions: false }
      );

      if (confirmError) {
        return confirmError;
      }
      else {
        if (paymentIntent.status === "requires_action") {
          const { paymentIntent, error } = await stripe.confirmCardPayment(intent_client_secret);
          if (error) {
            //payment failed
          } else {
            return paymentIntent;
          }
        }
        else {
          return paymentIntent;
        }
      }
    }
  }));

  const options = useOptions(paymentRequest);

  if (buttonLoading) {
    return <H6 content={`Please wait ${googleApplePayLabel} pay is loading . . .`}/>
  }
  if (!buttonLoading && !paymentRequest) {
    return (
      <div style={{ fontSize: '15px', color: '#b62323' }}>Either your browser does not support the selected payment method or you do not have a saved wallet/payment card.</div>
    );
  }

  return (
    <PaymentRequestButtonElement
      className="PaymentRequestButton"
      options={options}
    />
  );
});

//  PaymentRequestForm;
